import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useT } from '../i18n';
import { BILLING_STOP_RULE } from '../i18n/billingStop';
import { BACKEND_URL } from '../runtimeConfig';

// Same trim as AuthForm: importing App here would create a circular import.
const API = `${BACKEND_URL}/api`;

const PLAN_LABELS = { start: 'Start', pro: 'Pro', team: 'Team', network: 'Network', enterprise: 'Enterprise' };
const INTERVALS = ['monthly', 'semiannual', 'annual'];

function intervalLabel(t, key) {
  if (key === 'monthly') return t('Monthly');
  if (key === 'semiannual') return `6 ${t('months')} −10%`;
  return `12 ${t('months')} −20%`;
}

/*
  First step of signup: registration is only possible with a plan. Start and
  Pro come with a free trial (card required in Stripe); Team and Network are
  billed on the day of signup. The choice is handed back via onContinue.
*/
export default function SignupPlanPicker({ initial, onContinue }) {
  const { t } = useT();
  const [catalog, setCatalog] = useState(null);
  const [failed, setFailed] = useState(false);
  const [plan, setPlan] = useState((initial && initial.plan) || 'pro');
  const [interval, setInterval_] = useState((initial && initial.interval) || 'monthly');

  const load = () => {
    setFailed(false);
    axios
      .get(`${API}/billing/plans`)
      .then((r) => setCatalog(r.data || {}))
      .catch(() => setFailed(true));
  };
  useEffect(load, []);

  if (failed) {
    return (
      <div className="flex flex-col gap-3 text-sm" data-testid="signup-plans-error">
        <p className="text-muted-foreground">{t('Could not load plans.')}</p>
        <button type="button" onClick={load} className="self-start rounded-lg border border-border px-4 py-2 hover:border-primary/50">
          {t('Try again')}
        </button>
      </div>
    );
  }
  if (!catalog) {
    return <p className="text-sm text-muted-foreground">{t('Loading…')}</p>;
  }

  const plans = catalog.plans || [];
  const trialDays = Number(catalog.trial_days) || 0;

  // Enterprise is sold monthly or for 12 months: a 6-month choice falls back
  // to 12 months for it, which is what its row shows.
  const continueWith = () => {
    const sel = plans.find((x) => x.key === plan);
    const ok = !sel || !Array.isArray(sel.intervals) || sel.intervals.includes(interval);
    onContinue({ plan, interval: ok ? interval : 'annual' });
  };

  return (
    <div className="flex flex-col gap-4" data-testid="signup-plan-picker">
      <div>
        <h3 className="text-base font-semibold text-foreground">{t('Choose your plan')}</h3>
        <p className="mt-1 text-xs text-muted-foreground">{t('Every plan includes the whole tracker.')}</p>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {INTERVALS.map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => setInterval_(key)}
            className={
              'rounded-lg border px-3 py-1.5 text-xs transition-colors ' +
              (interval === key
                ? 'border-primary bg-primary/10 font-medium text-primary'
                : 'border-border text-muted-foreground hover:border-primary/40 hover:text-foreground')
            }
            data-testid={`signup-interval-${key}`}
          >
            {intervalLabel(t, key)}
          </button>
        ))}
      </div>

      <div className="flex flex-col gap-2" role="radiogroup" aria-label={t('Choose your plan')}>
        {plans.map((p) => {
          const allowed = Array.isArray(p.intervals) ? p.intervals : INTERVALS;
          const offered = allowed.includes(interval);
          const shownInterval = offered ? interval : 'annual';
          const pricing = (p.pricing && p.pricing[shownInterval]) || {};
          const selected = plan === p.key;
          const trial = trialDays > 0 && Number(p.trial_days) > 0;
          return (
            <button
              key={p.key}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => {
                setPlan(p.key);
                if (!offered) setInterval_('annual');
              }}
              className={
                'flex items-start justify-between gap-3 rounded-xl border px-4 py-3 text-left transition-colors ' +
                (selected ? 'border-primary bg-primary/[0.06] ring-1 ring-primary/30' : 'border-border hover:border-primary/40')
              }
              data-testid={`signup-plan-${p.key}`}
            >
              <span className="min-w-0">
                <span className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-foreground">{p.label}</span>
                  <span
                    className={
                      'rounded-full px-2 py-0.5 text-[10px] font-medium ' +
                      (trial ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground')
                    }
                  >
                    {trial ? t('{n} days free', { n: trialDays }) : t('Billed today')}
                  </span>
                </span>
                {p.tagline ? <span className="mt-0.5 block text-xs text-muted-foreground">{t(p.tagline)}</span> : null}
              </span>
              <span className="shrink-0 text-right">
                <span className="block text-sm font-semibold text-foreground">
                  ${pricing.per_month_usd ?? p.monthly_usd}
                  <span className="text-xs font-normal text-muted-foreground"> / {t('month')}</span>
                </span>
                {shownInterval !== 'monthly' && pricing.total_usd ? (
                  <span className="block text-[10px] text-muted-foreground">
                    ${pricing.total_usd} {t('billed every')} {pricing.months} {t('months')}
                  </span>
                ) : null}
                {!offered ? (
                  <span className="block text-[10px] text-muted-foreground">{t('Monthly or 12 months')}</span>
                ) : null}
              </span>
            </button>
          );
        })}
      </div>

      {trialDays > 0 ? (
        <p className="text-xs leading-relaxed text-muted-foreground">
          {t('Start and Pro: {n} days free, card required. Charged automatically on day {next} unless you cancel. One free trial per account.', { n: trialDays, next: trialDays + 1 })}
        </p>
      ) : null}
      <p className="text-xs leading-relaxed text-muted-foreground">{t(BILLING_STOP_RULE)}</p>

      <button
        type="button"
        onClick={continueWith}
        className="w-full rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-all hover:brightness-110"
        data-testid="signup-plan-continue"
      >
        {t('Continue')}
      </button>
    </div>
  );
}

export function SignupPlanSummary({ plan, onChange }) {
  const { t } = useT();
  if (!plan) return null;
  return (
    <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-border px-4 py-2.5 text-sm" data-testid="signup-plan-summary">
      <span className="text-muted-foreground">
        {t('Plan')}: <span className="font-medium text-foreground">{PLAN_LABELS[plan.plan] || plan.plan}</span>
        {' · '}
        {intervalLabel(t, plan.interval)}
      </span>
      <button type="button" onClick={onChange} className="text-xs font-medium text-primary hover:underline">
        {t('Change')}
      </button>
    </div>
  );
}
